import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

/**
 * Formater une date pour l'axe X
 */
const formatDateLabel = (dateStr, period, language = 'fr') => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const locale = language === 'fr' ? 'fr-FR' : 'en-US';
  
  if (period === '7') {
    return date.toLocaleDateString(locale, { weekday: 'short' });
  } else if (period === '30') {
    return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
  } else {
    return date.toLocaleDateString(locale, { month: 'short' });
  }
};

/**
 * Formater les crédits pour l'axe Y
 */
const formatCredits = (value) => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return value.toString();
};

/**
 * Générer les données groupées par jour
 */
const aggregateByDay = (data, periodDays) => {
  if (!data || data.length === 0) return [];

  const dayMap = new Map();
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - parseInt(periodDays, 10) + 1);
  startDate.setHours(0, 0, 0, 0);

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().split('T')[0];
    dayMap.set(key, {
      date: key,
      earned: 0,
      spent: 0,
      balance: null,
      transactions: 0,
    });
  }

  let lastBalance = 0;
  data.forEach((tx) => {
    const txDate = new Date(tx.timestamp);
    const key = txDate.toISOString().split('T')[0];
    
    if (dayMap.has(key)) {
      const day = dayMap.get(key);
      const amount = tx.amount || 0;
      
      if (amount > 0) {
        day.earned += amount;
      } else {
        day.spent += Math.abs(amount);
      }
      day.transactions++;
      
      if (tx.balance !== undefined && tx.balance !== null) {
        day.balance = tx.balance;
        lastBalance = tx.balance;
      } else if (tx.balance_after !== undefined && tx.balance_after !== null) {
        day.balance = tx.balance_after;
        lastBalance = tx.balance_after;
      }
    }
  });

  const result = Array.from(dayMap.values());
  let runningBalance = lastBalance;
  for (let i = result.length - 1; i >= 0; i--) {
    if (result[i].balance !== null) {
      runningBalance = result[i].balance;
    } else {
      result[i].balance = runningBalance;
    }
  }

  return result;
};

/**
 * CreditChart - Graphique d'évolution avec axes et dates
 */
const CreditChart = ({
  data = [],
  period = '30',
  onPeriodChange,
  isLoading = false,
  variant = 'full',
  className = '',
}) => {
  const { t, i18n } = useTranslation('credits');
  const width = 400;
  const height = 200;
  const margin = useMemo(() => ({ top: 20, right: 20, bottom: 35, left: 50 }), []);
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const dailyData = useMemo(() => aggregateByDay(data, period), [data, period]);

  const chartInfo = useMemo(() => {
    if (dailyData.length === 0) {
      return {
        maxBalance: 100,
        minBalance: 0,
        maxTransaction: 10,
        totalAcquired: 0,
        totalSpent: 0,
        yTicks: [0, 50, 100],
      };
    }

    const balances = dailyData.map(d => d.balance).filter(b => b !== null);
    const transactions = dailyData.flatMap(d => [d.earned, d.spent]).filter(t => t > 0);
    
    const maxBal = Math.max(...balances, 100);
    const minBal = Math.min(...balances, 0);
    const maxTx = transactions.length > 0 ? Math.max(...transactions) : 10;
    
    const totalAcq = dailyData.reduce((sum, d) => sum + d.earned, 0);
    const totalSpt = dailyData.reduce((sum, d) => sum + d.spent, 0);

    const range = maxBal - minBal;
    const step = Math.ceil(range / 4 / 10) * 10 || 25;
    const ticks = [];
    for (let y = Math.floor(minBal / step) * step; y <= maxBal + step; y += step) {
      if (y >= 0) ticks.push(y);
    }
    if (ticks.length > 6) {
      const newStep = step * 2;
      ticks.length = 0;
      for (let y = 0; y <= maxBal + newStep; y += newStep) {
        ticks.push(y);
      }
    }

    return { 
      maxBalance: Math.max(...ticks), 
      minBalance: Math.min(...ticks, 0), 
      maxTransaction: maxTx, 
      totalAcquired: totalAcq, 
      totalSpent: totalSpt, 
      yTicks: ticks 
    };
  }, [dailyData]);

  const { maxBalance, minBalance, maxTransaction, totalAcquired, totalSpent, yTicks } = chartInfo;

  const xLabels = useMemo(() => {
    if (dailyData.length === 0) return [];
    
    const step = Math.max(1, Math.floor(dailyData.length / 6));
    const labels = [];
    
    for (let i = 0; i < dailyData.length; i += step) {
      labels.push({
        index: i,
        label: formatDateLabel(dailyData[i].date, period, i18n.language),
      });
    }
    
    const lastIdx = dailyData.length - 1;
    if (!labels.find(l => l.index === lastIdx)) {
      labels.push({
        index: lastIdx,
        label: formatDateLabel(dailyData[lastIdx].date, period, i18n.language),
      });
    }
    
    return labels;
  }, [dailyData, period, i18n.language]);

  const xScale = (index) => {
    if (dailyData.length <= 1) return margin.left + chartWidth / 2;
    return margin.left + (index / (dailyData.length - 1)) * chartWidth;
  };

  const yScale = (value) => {
    const range = maxBalance - minBalance || 1;
    return margin.top + ((maxBalance - value) / range) * chartHeight;
  };

  const balancePath = useMemo(() => {
    if (dailyData.length < 2) return '';
    return dailyData.map((day, i) => {
      const x = margin.left + (i / (dailyData.length - 1)) * chartWidth;
      const range = maxBalance - minBalance || 1;
      const y = margin.top + ((maxBalance - (day.balance || 0)) / range) * chartHeight;
      return i === 0 ? `M ${x},${y}` : ` L ${x},${y}`;
    }).join('');
  }, [dailyData, maxBalance, minBalance, chartWidth, chartHeight, margin]);

  const areaPath = useMemo(() => {
    if (dailyData.length < 2) return '';
    const baseY = margin.top + chartHeight;
    const range = maxBalance - minBalance || 1;
    let path = `M ${margin.left},${baseY}`;
    dailyData.forEach((day, i) => {
      const x = margin.left + (i / (dailyData.length - 1)) * chartWidth;
      const y = margin.top + ((maxBalance - (day.balance || 0)) / range) * chartHeight;
      path += ` L ${x},${y}`;
    });
    path += ` L ${margin.left + chartWidth},${baseY} Z`;
    return path;
  }, [dailyData, maxBalance, minBalance, chartWidth, chartHeight, margin]);

  const periodLabels = {
    '7': t('chart.period7days', '7 jours'),
    '30': t('chart.period30days', '30 jours'),
    '90': t('chart.period90days', '3 mois'),
  };

  if (isLoading) {
    return (
      <div className={`rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/50 p-5 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between">
            <div className="h-5 w-40 bg-slate-200 dark:bg-white/10 rounded" />
            <div className="h-7 w-28 bg-slate-200 dark:bg-white/10 rounded-lg" />
          </div>
          <div className="h-48 bg-slate-100 dark:bg-white/5 rounded-xl" />
        </div>
      </div>
    );
  }

  if (variant === 'simple') {
    return (
      <div className={`rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/50 p-5 ${className}`}>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
          {t('chart.activityTitle', 'Activité')} ({periodLabels[period]})
        </h3>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-emerald-500" />
            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">+{totalAcquired} {t('chart.acquired', 'acquis')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-rose-500" />
            <span className="text-sm font-medium text-rose-500 dark:text-rose-400">-{totalSpent} {t('chart.spent', 'utilisés')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/50 p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('chart.title', 'Évolution des crédits')}</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{periodLabels[period]}</p>
        </div>
        {onPeriodChange && (
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-white/5 rounded-lg p-1">
            {['7', '30', '90'].map((p) => (
              <button
                key={p}
                onClick={() => onPeriodChange(p)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  period === p
                    ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                {p}j
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative">
        {dailyData.length >= 2 ? (
          <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" className="overflow-visible">
            <defs>
              <linearGradient id="creditBalanceGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#6366f1" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#6366f1" stopOpacity="0.02" />
              </linearGradient>
            </defs>

            {yTicks.map((tick, i) => (
              <line key={`grid-${i}`} x1={margin.left} y1={yScale(tick)} x2={width - margin.right} y2={yScale(tick)} stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" className="text-slate-400" />
            ))}

            {yTicks.map((tick, i) => (
              <text key={`y-${i}`} x={margin.left - 8} y={yScale(tick)} textAnchor="end" dominantBaseline="middle" className="fill-slate-400 dark:fill-slate-500 text-[10px] font-medium">
                {formatCredits(tick)}
              </text>
            ))}

            {xLabels.map((label, i) => (
              <g key={`x-${i}`}>
                <line x1={xScale(label.index)} y1={margin.top} x2={xScale(label.index)} y2={margin.top + chartHeight} stroke="currentColor" strokeOpacity="0.05" strokeWidth="1" className="text-slate-400" />
                <text x={xScale(label.index)} y={height - 8} textAnchor="middle" className="fill-slate-400 dark:fill-slate-500 text-[10px] font-medium">
                  {label.label}
                </text>
              </g>
            ))}

            {/* Barres partant du bas (ligne zéro) */}
            {dailyData.map((day, index) => {
              if (day.earned === 0 && day.spent === 0) return null;
              const x = xScale(index);
              const barWidth = Math.max(6, Math.min(14, chartWidth / dailyData.length * 0.7));
              const baselineY = yScale(0);
              const maxBarH = chartHeight * 0.6;
              
              const earnedHeight = Math.max(4, (day.earned / maxTransaction) * maxBarH);
              const spentHeight = Math.max(4, (day.spent / maxTransaction) * maxBarH);
              
              return (
                <g key={`bar-${index}`}>
                  {day.earned > 0 && (
                    <rect 
                      x={x - barWidth - 1} 
                      y={baselineY - earnedHeight} 
                      width={barWidth} 
                      height={earnedHeight} 
                      rx={3} 
                      fill="#10b981" 
                      opacity="0.85" 
                    />
                  )}
                  {day.spent > 0 && (
                    <rect 
                      x={x + 1} 
                      y={baselineY - spentHeight} 
                      width={barWidth} 
                      height={spentHeight} 
                      rx={3} 
                      fill="#f43f5e" 
                      opacity="0.85" 
                    />
                  )}
                </g>
              );
            })}

            <path d={areaPath} fill="url(#creditBalanceGradient)" />
            <path d={balancePath} fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

            {dailyData.map((day, index) => {
              if (day.transactions === 0) return null;
              return <circle key={`pt-${index}`} cx={xScale(index)} cy={yScale(day.balance)} r={4} fill="#6366f1" stroke="white" strokeWidth={2} className="dark:stroke-slate-900" />;
            })}

            <circle cx={xScale(dailyData.length - 1)} cy={yScale(dailyData[dailyData.length - 1]?.balance || 0)} r={5} fill="#6366f1" stroke="white" strokeWidth={2.5} className="dark:stroke-slate-900" />

            <g>
              <rect x={xScale(dailyData.length - 1) + 8} y={yScale(dailyData[dailyData.length - 1]?.balance || 0) - 10} width={45} height={20} rx={4} fill="#6366f1" />
              <text x={xScale(dailyData.length - 1) + 30} y={yScale(dailyData[dailyData.length - 1]?.balance || 0) + 1} textAnchor="middle" dominantBaseline="middle" className="fill-white text-[10px] font-bold">
                {dailyData[dailyData.length - 1]?.balance || 0}
              </text>
            </g>
          </svg>
        ) : (
          <div className="h-48 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
            <FontAwesomeIcon icon="chart-line" className="text-3xl mb-2 opacity-30" />
            <span className="text-sm">{t('chart.notEnoughData', 'Pas assez de données')}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-white/5 mt-4">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-emerald-500" />
            <span className="text-xs text-slate-600 dark:text-slate-400">{t('chart.acquired', 'Acquis')}</span>
            <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">+{totalAcquired}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-rose-500" />
            <span className="text-xs text-slate-600 dark:text-slate-400">{t('chart.spent', 'Dépensés')}</span>
            <span className="text-sm font-bold text-rose-500 dark:text-rose-400">-{totalSpent}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-0.5 rounded bg-indigo-500" />
          <span className="text-[11px] text-slate-400 dark:text-slate-500 font-medium">{t('chart.balance', 'Solde')}</span>
        </div>
      </div>
    </div>
  );
};

export default CreditChart;
